import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import {
  type ExecuteSavedQueryFieldValue,
  executeSavedQuery,
  getSavedQueryById,
  type SavedQueryFieldDto,
} from '../CustomDash/api/saved-queries-api';
import DashboardPage from '../DashboardPage';
import styles from './SnowflakeQueryPlaygroundPage.module.css';
import {
  SNOWFLAKE_QUERY_PLAYGROUND_QUERIES,
  type SnowflakePlaygroundStageId,
} from './snowflake-query-playground-queries';

/** Upper bound for sentiment MSA `top_n` (matches dash-meta saved query `LEAST(..., 20)`). */
const SENTIMENT_MSA_TOP_N_MAX = 20;

function formatCell(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

function isEmptyForColumnFilter(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }
  return false;
}

function columnsWithAnyNonEmptyValue(
  rows: Record<string, unknown>[],
  keys: string[]
): string[] {
  return keys.filter((key) =>
    rows.some((row) => !isEmptyForColumnFilter(row[key]))
  );
}

function buildPlaygroundFieldValues(
  fields: SavedQueryFieldDto[],
  input: {
    rcidRaw: string;
    competitorCsv: string;
    topNRaw: string;
    customTaxIdRaw: string;
  }
): Record<string, ExecuteSavedQueryFieldValue> {
  const names = new Set(fields.map((f) => f.name));
  const out: Record<string, ExecuteSavedQueryFieldValue> = {};

  if (names.has('rcid')) {
    const rcid = Number(input.rcidRaw.trim());
    if (Number.isNaN(rcid)) {
      throw new Error('Company rcid must be a valid number.');
    }
    out.rcid = rcid;
  }

  if (names.has('competitor_rcids')) {
    const competitorParts = input.competitorCsv
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    out.competitor_rcids = competitorParts
      .map((p) => Number(p))
      .filter((n) => !Number.isNaN(n));
  }

  if (names.has('top_n')) {
    const top_nParsed = Number(input.topNRaw.trim());
    out.top_n = Number.isNaN(top_nParsed)
      ? SENTIMENT_MSA_TOP_N_MAX
      : Math.min(top_nParsed, SENTIMENT_MSA_TOP_N_MAX);
  }

  if (names.has('custom_tax_id')) {
    const t = input.customTaxIdRaw.trim();
    out.custom_tax_id = t.length > 0 ? t : 'default';
  }

  return out;
}

const SnowflakeQueryPlaygroundPage = () => {
  const [stageId, setStageId] = useState<SnowflakePlaygroundStageId>(
    SNOWFLAKE_QUERY_PLAYGROUND_QUERIES[0].id
  );
  const [rcidRaw, setRcidRaw] = useState('1359692');
  const [competitorCsv, setCompetitorCsv] = useState(
    '832518, 22148737, 1370, 929633, 857255, 22149845, 1114699, 77107, 167546'
  );
  const [topNRaw, setTopNRaw] = useState('20');
  const [customTaxIdRaw, setCustomTaxIdRaw] = useState('default');

  const savedQueryQuery = useQuery({
    queryKey: ['snowflake-query-playground', 'saved-query', stageId],
    queryFn: async () => {
      const record = await getSavedQueryById(stageId);
      if (record === undefined) {
        throw new Error('Saved query not found.');
      }
      return record;
    },
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const record = savedQueryQuery.data;
      if (record === undefined) {
        throw new Error('Saved query not loaded.');
      }
      const fieldValues =
        record.fields.length === 0
          ? {}
          : buildPlaygroundFieldValues(record.fields, {
              rcidRaw,
              competitorCsv,
              topNRaw,
              customTaxIdRaw,
            });
      const rows = await executeSavedQuery(stageId, fieldValues);
      return (rows ?? []) as Record<string, unknown>[];
    },
  });

  const columns = useMemo(() => {
    const list = mutation.data ?? [];
    const first = list[0];
    if (first === undefined || typeof first !== 'object' || first === null) {
      return [];
    }
    return columnsWithAnyNonEmptyValue(list, Object.keys(first));
  }, [mutation.data]);

  const rows = mutation.data ?? [];

  return (
    <DashboardPage
      title={['Snowflake query playground']}
      backPath="/tools"
      loading={false}
      scrollY
    >
      <div className={styles.pageBounds}>
        <div className={styles.container}>
          <p className={styles.meta}>
            Admin-only. Runs registered dash-meta saved queries only (no ad-hoc
            SQL). The example query reads one row from
            SNOWFLAKE.INFORMATION_SCHEMA (built-in database; no binds). Queries
            that declare template fields show rcid / competitor_rcids / top_n
            (capped at 20) / custom_tax_id inputs below.
          </p>

          <div className={styles.controls}>
            <div className={styles.fieldGrow}>
              <label className={styles.label} htmlFor="playground-stage">
                Stage
              </label>
              <select
                id="playground-stage"
                className={styles.select}
                value={stageId}
                onChange={(e) =>
                  setStageId(e.target.value as SnowflakePlaygroundStageId)
                }
              >
                {SNOWFLAKE_QUERY_PLAYGROUND_QUERIES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.sqlSection}>
              <span className={styles.label}>
                Query template
                {savedQueryQuery.isSuccess &&
                savedQueryQuery.data.fields.length > 0
                  ? ' (binds: {{rcid}}, etc.)'
                  : ''}
              </span>
              {savedQueryQuery.isPending ? (
                <p className={styles.meta}>Loading SQL…</p>
              ) : null}
              {savedQueryQuery.isError ? (
                <p className={styles.error} role="alert">
                  {savedQueryQuery.error instanceof Error
                    ? savedQueryQuery.error.message
                    : 'Could not load query SQL.'}
                </p>
              ) : null}
              {savedQueryQuery.isSuccess &&
              savedQueryQuery.data.queryString !== undefined &&
              savedQueryQuery.data.queryString.length > 0 ? (
                <pre
                  className={styles.sqlPre}
                  aria-label="Saved query SQL template"
                >
                  {savedQueryQuery.data.queryString}
                </pre>
              ) : null}
              {savedQueryQuery.isSuccess &&
              (savedQueryQuery.data.queryString === undefined ||
                savedQueryQuery.data.queryString.length === 0) ? (
                <p className={styles.empty}>
                  No queryString on this saved query.
                </p>
              ) : null}
            </div>

            <div className={styles.row}>
              {savedQueryQuery.isSuccess &&
              savedQueryQuery.data.fields.length > 0 ? (
                <>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="playground-rcid">
                      rcid
                    </label>
                    <input
                      id="playground-rcid"
                      className={styles.input}
                      value={rcidRaw}
                      onChange={(e) => setRcidRaw(e.target.value)}
                      inputMode="numeric"
                      autoComplete="off"
                    />
                  </div>
                  <div className={styles.fieldGrow}>
                    <label
                      className={styles.label}
                      htmlFor="playground-competitors"
                    >
                      competitor_rcids (comma-separated, optional)
                    </label>
                    <input
                      id="playground-competitors"
                      className={styles.input}
                      value={competitorCsv}
                      onChange={(e) => setCompetitorCsv(e.target.value)}
                      placeholder="e.g. 123, 456"
                      autoComplete="off"
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="playground-top-n">
                      top_n (max {SENTIMENT_MSA_TOP_N_MAX})
                    </label>
                    <input
                      id="playground-top-n"
                      className={styles.input}
                      value={topNRaw}
                      onChange={(e) => setTopNRaw(e.target.value)}
                      inputMode="numeric"
                      max={SENTIMENT_MSA_TOP_N_MAX}
                      autoComplete="off"
                    />
                  </div>
                  {savedQueryQuery.data.fields.some(
                    (f) => f.name === 'custom_tax_id'
                  ) ? (
                    <div className={styles.fieldGrow}>
                      <label
                        className={styles.label}
                        htmlFor="playground-custom-tax-id"
                      >
                        custom_tax_id (taxonomy_name, e.g. default, ONET)
                      </label>
                      <input
                        id="playground-custom-tax-id"
                        className={styles.input}
                        value={customTaxIdRaw}
                        onChange={(e) => setCustomTaxIdRaw(e.target.value)}
                        placeholder="default"
                        autoComplete="off"
                      />
                    </div>
                  ) : null}
                </>
              ) : null}
              <button
                type="button"
                className={styles.button}
                disabled={
                  mutation.isPending ||
                  !savedQueryQuery.isSuccess ||
                  savedQueryQuery.isError
                }
                onClick={() => {
                  mutation.reset();
                  mutation.mutate();
                }}
              >
                {mutation.isPending ? 'Running…' : 'Run query'}
              </button>
            </div>
          </div>

          {mutation.isError ? (
            <p className={styles.error} role="alert">
              {mutation.error instanceof Error
                ? mutation.error.message
                : 'Request failed'}
            </p>
          ) : null}

          {mutation.isSuccess ? (
            <p className={styles.meta}>
              {rows.length} row{rows.length === 1 ? '' : 's'} · savedQueryId{' '}
              <code>{stageId}</code>
            </p>
          ) : null}

          {mutation.isSuccess && columns.length === 0 ? (
            <p className={styles.empty}>No columns (empty result set).</p>
          ) : null}

          {mutation.isSuccess && columns.length > 0 ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i}>
                      {columns.map((col) => (
                        <td key={col} title={formatCell(row[col])}>
                          {formatCell(row[col])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </DashboardPage>
  );
};

export default SnowflakeQueryPlaygroundPage;
